/**
 * Board layout persistence — server of record, local mirror for the booth.
 *
 * Two scopes per (tenant, screen), matching `board_layouts` (migrations 0037 +
 * 0048):
 *   `user`  the caller's own arrangements
 *   `team`  the team's layouts, written by a Team Admin, read by every member
 * Each scope holds several NAMED layouts per screen (`SavedLayout`), plus the
 * legacy unnamed slot older clients wrote. The layout the user last picked is
 * the ACTIVE one (`board_layout_active` on the server, mirrored in
 * localStorage so the choice survives reloads and an offline booth).
 * Resolution: active → user slot → team slot → first team layout → first
 * user layout → the curated default the screen registers.
 *
 * Reads are synchronous from an in-memory cache so the board can render on
 * first paint. `hydrateLayouts()` fills that cache from `GET /users/v1/layouts`
 * once per session and notifies subscribers; saves write through the API and
 * update the cache (and a localStorage mirror keyed by user + tenant, so a
 * booth that lost the network mid-game still opens on its own board). The
 * mirror is only consulted before hydration succeeds - the server wins.
 *
 * Keys: gridiron.layout.v2:{scope}:{userId}:{tenantId}:{screenId}      (unnamed slot)
 *       gridiron.layout-list.v1:{userId}:{tenantId}:{screenId}       (named layouts)
 *       gridiron.layout-active.v1:{userId}:{tenantId}:{screenId}     (selected id)
 */
import type { ApiClient } from '../lib/api-client';
import type { BoardLayoutRecord, LayoutScope } from '../lib/api-types';

import { parseBoardLayout } from './board-geometry';
import type { BoardLayout, LayoutOwnershipScope, LayoutRepoScope } from './types';

export const LAYOUT_STORAGE_PREFIX = 'gridiron.layout.v2';
export const LAYOUT_LIST_STORAGE_PREFIX = 'gridiron.layout-list.v1';
export const LAYOUT_ACTIVE_STORAGE_PREFIX = 'gridiron.layout-active.v1';

/** A screen for one user: the key every named-layout call is made against. */
export interface LayoutScreenScope {
  userId: string;
  tenantId: string;
  screenId: string;
}

/** One saved layout of a screen, as the Layout dropdown lists it. */
export interface SavedLayout {
  /** Server id (`board_layouts.id`); `local:*` until the server answers. */
  id: string;
  scope: LayoutOwnershipScope;
  screenId: string;
  /** The stored name ('' = the legacy unnamed slot). */
  layoutName: string;
  /** What the dropdown shows: the name, or "My layout" / "Team default" for a legacy slot. */
  label: string;
  layout: BoardLayout;
  updatedAt: string;
}

/** Why a named-layout write failed, in words the dialog can show. */
export class LayoutWriteError extends Error {
  constructor(
    message: string,
    readonly code: 'name_taken' | 'forbidden' | 'not_found' | 'server'
  ) {
    super(message);
  }
}

export function layoutListStorageKey(scope: LayoutScreenScope): string {
  return `${LAYOUT_LIST_STORAGE_PREFIX}:${scope.userId}:${scope.tenantId}:${scope.screenId}`;
}

export function layoutActiveStorageKey(scope: LayoutScreenScope): string {
  return `${LAYOUT_ACTIVE_STORAGE_PREFIX}:${scope.userId}:${scope.tenantId}:${scope.screenId}`;
}

function screenKey(scope: LayoutScreenScope): string {
  return `${scope.userId}:${scope.tenantId}:${scope.screenId}`;
}

/** User layouts first, then team layouts; each alphabetical. */
function sortSaved(list: SavedLayout[]): SavedLayout[] {
  return [...list].sort((a, b) =>
    a.scope === b.scope
      ? a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      : a.scope === 'user'
        ? -1
        : 1
  );
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function toWriteError(error: unknown, name: string): LayoutWriteError {
  const status = (error as { status?: number } | null)?.status;
  if (status === 409) {
    return new LayoutWriteError(
      `A layout named "${name}" already exists for this screen. Pick another name.`,
      'name_taken'
    );
  }
  if (status === 403) {
    return new LayoutWriteError('Only a Team Admin can change team layouts.', 'forbidden');
  }
  if (status === 404) return new LayoutWriteError('That layout no longer exists.', 'not_found');
  return new LayoutWriteError('The server did not accept the layout. Try again.', 'server');
}

/**
 * The scope the Customize strip's primary Save writes to: the caller's OWN
 * layouts (`board_layouts.scope = 'user'`, `owner_user_id` = the caller). Only
 * "Save as team default" (Team Admin) writes `TEAM_SAVE_SCOPE`.
 */
export const PERSONAL_SAVE_SCOPE = 'user' as const satisfies LayoutOwnershipScope;
export const TEAM_SAVE_SCOPE = 'team' as const satisfies LayoutOwnershipScope;

export const OWNERSHIP_LABELS: Readonly<Record<LayoutOwnershipScope, string>> = {
  team: 'Team default',
  user: 'My layout',
};

export function layoutStorageKey(scope: LayoutRepoScope): string {
  return `${LAYOUT_STORAGE_PREFIX}:${scope.ownership}:${scope.userId}:${scope.tenantId}:${scope.screenId}`;
}

function cacheKey(scope: LayoutRepoScope): string {
  // A team row is one per (tenant, screen) - the user id is not part of it.
  const owner = scope.ownership === 'team' ? '-' : scope.userId;
  return `${scope.ownership}:${owner}:${scope.tenantId}:${scope.screenId}`;
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function parseStored(raw: unknown): BoardLayout | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const unit = (raw as { unit?: unknown }).unit;
  return typeof unit === 'string' ? parseBoardLayout(raw, unit) : null;
}

type Listener = () => void;

export class LayoutRepository {
  private readonly cache = new Map<string, BoardLayout>();
  /** Named layouts per user + tenant + screen (user rows + team rows). */
  private readonly saved = new Map<string, SavedLayout[]>();
  /** Selected layout id per user + tenant + screen (`null` = none chosen). */
  private readonly active = new Map<string, string | null>();
  private readonly listeners = new Set<Listener>();
  private hydratedFor: string | null = null;
  private inFlight: Promise<void> | null = null;
  /** Bumped on every change so React state can depend on it. */
  version = 0;

  constructor(private readonly apiClient: ApiClient | null) {}

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }

  /** Whether the server copy for this user + tenant has been loaded. */
  isHydrated(userId: string, tenantId: string): boolean {
    return this.hydratedFor === `${userId}:${tenantId}`;
  }

  /**
   * Load every layout the caller can see. Safe to call repeatedly: one fetch
   * per user + tenant, later calls await the same promise.
   */
  hydrate(userId: string, tenantId: string): Promise<void> {
    const key = `${userId}:${tenantId}`;
    if (this.hydratedFor === key) return Promise.resolve();
    if (this.inFlight !== null) return this.inFlight;
    if (this.apiClient === null) {
      this.hydratedFor = key;
      return Promise.resolve();
    }
    this.inFlight = this.apiClient
      .listLayouts()
      .then((page) => {
        // The server is the source of truth: drop whatever the mirror held for
        // this tenant, in memory and on disk, then rebuild both from the rows.
        for (const cached of [...this.cache.keys()]) {
          if (cached.includes(`:${tenantId}:`)) this.cache.delete(cached);
        }
        for (const cached of [...this.saved.keys()]) {
          if (cached.startsWith(`${userId}:${tenantId}:`)) this.saved.delete(cached);
        }
        this.clearMirror(tenantId);
        const lists = new Map<string, SavedLayout[]>();
        for (const record of page.items) {
          const layout = parseStored(record.layout);
          if (layout === null) continue;
          const layoutName = record.layout_name ?? '';
          const scope = this.scopeFor(record, userId, tenantId);
          if (layoutName === '') {
            this.cache.set(cacheKey(scope), layout);
            this.mirror(scope, layout);
          }
          const base = { userId, tenantId, screenId: record.screen_id };
          const entry = toSavedLayout(record, layout);
          lists.set(screenKey(base), [...(lists.get(screenKey(base)) ?? []), entry]);
        }
        for (const [key, list] of lists) {
          const [, , ...rest] = key.split(':');
          const base = { userId, tenantId, screenId: rest.join(':') };
          this.saved.set(key, sortSaved(list));
          this.mirrorList(base);
        }
        // The server's selection wins where it has one; a local pick made
        // offline survives until the server knows better.
        for (const [screenId, layoutId] of Object.entries(page.active ?? {})) {
          const base = { userId, tenantId, screenId };
          this.active.set(screenKey(base), layoutId);
          this.mirrorActive(base, layoutId);
        }
        this.hydratedFor = key;
        this.notify();
      })
      .catch(() => {
        // Offline: keep serving the mirror; the next hydrate() retries.
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }

  private scopeFor(record: BoardLayoutRecord, userId: string, tenantId: string): LayoutRepoScope {
    return {
      userId: record.scope === 'user' ? (record.owner_user_id ?? userId) : userId,
      tenantId,
      screenId: record.screen_id,
      ownership: record.scope,
    };
  }

  /** Forget every mirrored row for a tenant (before rebuilding from the server). */
  private clearMirror(tenantId: string): void {
    const store = storage();
    if (store === null) return;
    try {
      const doomed: string[] = [];
      for (let index = 0; index < store.length; index += 1) {
        const key = store.key(index);
        const isLayoutKey =
          key?.startsWith(`${LAYOUT_STORAGE_PREFIX}:`) === true ||
          key?.startsWith(`${LAYOUT_LIST_STORAGE_PREFIX}:`) === true;
        if (key !== null && isLayoutKey && key.includes(`:${tenantId}:`)) {
          doomed.push(key);
        }
      }
      for (const key of doomed) store.removeItem(key);
    } catch {
      /* non-fatal */
    }
  }

  private mirror(scope: LayoutRepoScope, layout: BoardLayout | null): void {
    const store = storage();
    if (store === null) return;
    try {
      if (layout === null) store.removeItem(layoutStorageKey(scope));
      else store.setItem(layoutStorageKey(scope), JSON.stringify(layout));
    } catch {
      /* private mode / quota - the server still has it */
    }
  }

  load(scope: LayoutRepoScope): BoardLayout | null {
    const cached = this.cache.get(cacheKey(scope));
    if (cached !== undefined) return cached;
    if (this.isHydrated(scope.userId, scope.tenantId)) return null;
    // Not hydrated yet (or offline): the mirror is the best we have.
    const store = storage();
    if (store === null) return null;
    try {
      const raw = store.getItem(layoutStorageKey(scope));
      return raw === null ? null : parseStored(JSON.parse(raw) as unknown);
    } catch {
      return null;
    }
  }

  // Board layouts are NAMED (saveNamed / renameSaved / deleteSaved). The old
  // single-slot save / clear (PUT / DELETE /layouts/{scope}/{screen}) is
  // retired for boards — the users service answers 409 LAYOUT_NAME_REQUIRED —
  // so the repository no longer offers it. Legacy unnamed rows still hydrate
  // (listed as "My layout" / "Team default") and are managed by id.

  // ------------------------------------------------------------------------
  // Named layouts (several per screen) + the active selection
  // ------------------------------------------------------------------------

  private mirrorList(scope: LayoutScreenScope): void {
    const store = storage();
    if (store === null) return;
    try {
      store.setItem(layoutListStorageKey(scope), JSON.stringify(this.saved.get(screenKey(scope))));
    } catch {
      /* non-fatal */
    }
  }

  private mirrorActive(scope: LayoutScreenScope, layoutId: string | null): void {
    const store = storage();
    if (store === null) return;
    try {
      if (layoutId === null) store.removeItem(layoutActiveStorageKey(scope));
      else store.setItem(layoutActiveStorageKey(scope), layoutId);
    } catch {
      /* non-fatal */
    }
  }

  /** Every saved layout of a screen the caller can load: theirs, then the team's. */
  listSaved(scope: LayoutScreenScope): SavedLayout[] {
    const cached = this.saved.get(screenKey(scope));
    if (cached !== undefined) return cached;
    if (this.isHydrated(scope.userId, scope.tenantId)) return [];
    const store = storage();
    if (store === null) return [];
    try {
      const raw = store.getItem(layoutListStorageKey(scope));
      if (raw === null) return [];
      const parsed = JSON.parse(raw) as SavedLayout[];
      return Array.isArray(parsed)
        ? parsed.flatMap((entry) => {
            const layout = parseStored(entry.layout);
            return layout === null ? [] : [{ ...entry, layout }];
          })
        : [];
    } catch {
      return [];
    }
  }

  /** The layout the user last selected on this screen (`null` = none chosen). */
  getActiveLayoutId(scope: LayoutScreenScope): string | null {
    const key = screenKey(scope);
    if (this.active.has(key)) return this.active.get(key) ?? null;
    const store = storage();
    try {
      return store?.getItem(layoutActiveStorageKey(scope)) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Select a saved layout (or `null` for none): cached + mirrored at once so
   * the board switches immediately, then remembered on the server so the
   * choice follows the user to another browser.
   */
  setActiveLayout(scope: LayoutScreenScope, layoutId: string | null): Promise<void> {
    this.active.set(screenKey(scope), layoutId);
    this.mirrorActive(scope, layoutId);
    this.notify();
    if (this.apiClient === null || (layoutId !== null && layoutId.startsWith('local:'))) {
      return Promise.resolve();
    }
    return this.apiClient.setActiveLayout(scope.screenId, layoutId).catch(() => undefined);
  }

  private replaceSaved(scope: LayoutScreenScope, list: SavedLayout[]): void {
    this.saved.set(screenKey(scope), sortSaved(list));
    this.mirrorList(scope);
  }

  /**
   * Save the board under a name in `ownership` scope. A layout of that scope
   * with the same name is saved over; otherwise a new one is created. The
   * result becomes the active layout. Optimistic: the list and selection
   * change at once and roll back if the server refuses (`LayoutWriteError`).
   */
  async saveNamed(
    scope: LayoutScreenScope,
    ownership: LayoutOwnershipScope,
    name: string,
    layout: BoardLayout
  ): Promise<SavedLayout> {
    const trimmed = name.trim();
    const before = this.listSaved(scope);
    const previousActive = this.getActiveLayoutId(scope);
    const existing = before.find(
      (entry) => entry.scope === ownership && sameName(entry.layoutName || entry.label, trimmed)
    );
    const optimistic: SavedLayout = {
      id: existing?.id ?? `local:${Math.random().toString(36).slice(2)}`,
      scope: ownership,
      screenId: scope.screenId,
      layoutName: trimmed,
      label: trimmed,
      layout: { ...layout, name: trimmed },
      updatedAt: new Date().toISOString(),
    };
    this.replaceSaved(scope, [...before.filter((entry) => entry.id !== optimistic.id), optimistic]);
    this.active.set(screenKey(scope), optimistic.id);
    this.mirrorActive(scope, optimistic.id);
    this.notify();
    if (this.apiClient === null) return optimistic;
    try {
      const record =
        existing !== undefined && !existing.id.startsWith('local:')
          ? await this.apiClient.updateLayoutById(existing.id, {
              layout_name: trimmed,
              layout: optimistic.layout,
            })
          : await this.apiClient.saveLayoutWithName(ownership, scope.screenId, {
              layout_name: trimmed,
              layout: optimistic.layout,
            });
      const saved = toSavedLayout(record, optimistic.layout);
      const current = this.listSaved(scope).filter((entry) => entry.id !== optimistic.id);
      this.replaceSaved(scope, [...current, saved]);
      if (this.getActiveLayoutId(scope) === optimistic.id) {
        await this.setActiveLayout(scope, saved.id);
      } else {
        this.notify();
      }
      return saved;
    } catch (error) {
      this.replaceSaved(scope, before);
      this.active.set(screenKey(scope), previousActive);
      this.mirrorActive(scope, previousActive);
      this.notify();
      throw toWriteError(error, trimmed);
    }
  }

  /** Rename one saved layout (name unique per scope + screen). */
  async renameSaved(scope: LayoutScreenScope, layoutId: string, name: string): Promise<void> {
    const trimmed = name.trim();
    const before = this.listSaved(scope);
    const target = before.find((entry) => entry.id === layoutId);
    if (target === undefined)
      throw new LayoutWriteError('That layout no longer exists.', 'not_found');
    const clash = before.find(
      (entry) =>
        entry.id !== layoutId &&
        entry.scope === target.scope &&
        sameName(entry.layoutName || entry.label, trimmed)
    );
    if (clash !== undefined) throw toWriteError({ status: 409 }, trimmed);
    this.replaceSaved(scope, [
      ...before.filter((entry) => entry.id !== layoutId),
      {
        ...target,
        layoutName: trimmed,
        label: trimmed,
        layout: { ...target.layout, name: trimmed },
      },
    ]);
    this.notify();
    if (this.apiClient === null || layoutId.startsWith('local:')) return;
    try {
      await this.apiClient.updateLayoutById(layoutId, { layout_name: trimmed });
    } catch (error) {
      this.replaceSaved(scope, before);
      this.notify();
      throw toWriteError(error, trimmed);
    }
  }

  /**
   * Delete one saved layout. When it was the active one, the next available
   * layout is selected automatically (`fallbackAfterDelete`: the caller's own
   * first, then the team's, then `null` = the default board), remembered on
   * the server (PUT /layouts/active/{screen}) and its id returned.
   */
  async deleteSaved(scope: LayoutScreenScope, layoutId: string): Promise<string | null> {
    const before = this.listSaved(scope);
    const previousActive = this.getActiveLayoutId(scope);
    const remaining = before.filter((entry) => entry.id !== layoutId);
    this.replaceSaved(scope, remaining);
    // A legacy unnamed slot also lives in the slot cache + mirror; drop it
    // there too, or resolveLayout would keep loading the deleted board.
    const target = before.find((entry) => entry.id === layoutId);
    const slotScope: LayoutRepoScope | null =
      target !== undefined && target.layoutName === ''
        ? { ...scope, ownership: target.scope }
        : null;
    const slotBefore = slotScope === null ? undefined : this.cache.get(cacheKey(slotScope));
    if (slotScope !== null) {
      this.cache.delete(cacheKey(slotScope));
      this.mirror(slotScope, null);
    }
    const wasActive = previousActive === layoutId;
    const nextActive = wasActive ? (fallbackAfterDelete(remaining)?.id ?? null) : previousActive;
    if (wasActive) {
      this.active.set(screenKey(scope), nextActive);
      this.mirrorActive(scope, nextActive);
    }
    this.notify();
    if (this.apiClient === null || layoutId.startsWith('local:')) return nextActive;
    try {
      await this.apiClient.deleteLayoutById(layoutId);
      if (wasActive) await this.setActiveLayout(scope, nextActive);
      return nextActive;
    } catch (error) {
      this.replaceSaved(scope, before);
      if (slotScope !== null && slotBefore !== undefined) {
        this.cache.set(cacheKey(slotScope), slotBefore);
        this.mirror(slotScope, slotBefore);
      }
      this.active.set(screenKey(scope), previousActive);
      this.mirrorActive(scope, previousActive);
      this.notify();
      throw toWriteError(error, '');
    }
  }

  exportJson(scope: LayoutRepoScope): string | null {
    const layout = this.load(scope);
    return layout === null ? null : JSON.stringify(layout, null, 2);
  }
}

let activeRepository = new LayoutRepository(null);

export function getLayoutRepository(): LayoutRepository {
  return activeRepository;
}

/** Install the API-backed repository (app bootstrap) or a test double. */
export function setLayoutRepository(next: LayoutRepository): void {
  activeRepository = next;
}

export function createLayoutRepository(apiClient: ApiClient | null): LayoutRepository {
  return new LayoutRepository(apiClient);
}

function toSavedLayout(record: BoardLayoutRecord, layout: BoardLayout): SavedLayout {
  const layoutName = record.layout_name ?? '';
  return {
    id: record.layout_id ?? record.id,
    scope: record.scope,
    screenId: record.screen_id,
    layoutName,
    label: layoutName || record.name || OWNERSHIP_LABELS[record.scope],
    layout,
    updatedAt: record.updated_at,
  };
}

/** The resolved board plus which saved layout (if any) it came from. */
export interface ResolvedLayout {
  layout: BoardLayout;
  ownership: LayoutOwnershipScope;
  /** The saved layout on screen (null = the curated default or an unlisted slot). */
  activeLayoutId: string | null;
  /** Name to show for what is loaded. */
  label: string;
}

/**
 * The name the Save dialog suggests: the active layout's when it is one of the
 * coach's own (keeping it saves over it), else "My layout" (numbered when the
 * coach already has one by that name).
 */
export function suggestLayoutName(saved: readonly SavedLayout[], activeId: string | null): string {
  const active = saved.find((entry) => entry.id === activeId);
  if (active?.scope === 'user') return active.label;
  const taken = new Set(
    saved.filter((entry) => entry.scope === 'user').map((entry) => entry.label.toLowerCase())
  );
  if (!taken.has('my layout')) return 'My layout';
  let index = 2;
  while (taken.has(`my layout ${index}`)) index += 1;
  return `My layout ${index}`;
}

/**
 * The layout that takes over when the active one is deleted: the next of the
 * caller's own layouts (My layouts, alphabetical), else the first team layout
 * (Team layouts, alphabetical), else `null` — the screen's default board.
 */
export function fallbackAfterDelete(remaining: readonly SavedLayout[]): SavedLayout | null {
  const byLabel = (a: SavedLayout, b: SavedLayout): number =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  const firstOf = (scope: LayoutOwnershipScope): SavedLayout | undefined =>
    remaining.filter((entry) => entry.scope === scope).sort(byLabel)[0];
  return firstOf('user') ?? firstOf('team') ?? null;
}

/** The label shown when no saved layout is loaded (the board's built-in default). */
export const DEFAULT_LAYOUT_LABEL = 'Standard board';

/**
 * Resolve the live layout: the active (selected) saved layout → user slot →
 * team slot → first team layout → first user layout → curated default.
 */
export function resolveLayout(args: {
  userId: string;
  tenantId: string;
  screenId: string;
  curatedDefault: BoardLayout;
}): ResolvedLayout {
  const repo = getLayoutRepository();
  const base = { userId: args.userId, tenantId: args.tenantId, screenId: args.screenId };
  const saved = repo.listSaved(base);
  const activeId = repo.getActiveLayoutId(base);
  const active = activeId === null ? undefined : saved.find((entry) => entry.id === activeId);
  const asResolved = (entry: SavedLayout): ResolvedLayout => ({
    layout: entry.layout,
    ownership: entry.scope,
    activeLayoutId: entry.id,
    label: entry.label,
  });
  if (active !== undefined) return asResolved(active);
  const slot = (ownership: LayoutOwnershipScope): ResolvedLayout | null => {
    const layout = repo.load({ ...base, ownership });
    if (layout === null) return null;
    const entry = saved.find((item) => item.scope === ownership && item.layoutName === '');
    return {
      layout,
      ownership,
      activeLayoutId: entry?.id ?? null,
      label: entry?.label ?? (layout.name.trim() || OWNERSHIP_LABELS[ownership]),
    };
  };
  const firstOf = (ownership: LayoutOwnershipScope): ResolvedLayout | null => {
    const entry = saved.find((item) => item.scope === ownership);
    return entry === undefined ? null : asResolved(entry);
  };
  return (
    slot('user') ??
    slot('team') ??
    firstOf('team') ??
    firstOf('user') ?? {
      layout: args.curatedDefault,
      ownership: 'team',
      activeLayoutId: null,
      label: DEFAULT_LAYOUT_LABEL,
    }
  );
}

/** The team's default for a screen (Reset): team slot → first team layout → curated. */
export function resolveTeamDefault(args: {
  userId: string;
  tenantId: string;
  screenId: string;
  curatedDefault: BoardLayout;
}): BoardLayout {
  const repo = getLayoutRepository();
  const base = { userId: args.userId, tenantId: args.tenantId, screenId: args.screenId };
  const slot = repo.load({ ...base, ownership: 'team' });
  if (slot !== null) return slot;
  return (
    repo.listSaved(base).find((entry) => entry.scope === 'team')?.layout ?? args.curatedDefault
  );
}

export type { LayoutScope };
